import { InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import { useConfirmationDialog, useCustomSnackbar } from '@procureai/ui-mui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Nullable } from 'ts-toolbelt/out/Union/Nullable';

import { IntakeCancelMutation, IntakeDiscardMutation } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { INTAKE_THREAD_LIST_QUERY } from '~/routes/IntakeList/IntakeListThreads.query.gql';

import { INTAKE_CANCEL_MUTATION, INTAKE_DISCARD_MUTATION } from './useIntakeRequestActions.mutation.gql';

type DisacrdCallback = (data?: Nullable<IntakeDiscardMutation>) => void;
type CancelCallback = (data?: Nullable<IntakeCancelMutation>) => void;

interface UseIntakePurchaseRequisitionActionsResult {
  discard: (id: string, onDiscarded?: DisacrdCallback) => void;
  cancel: (id: string, onCancled?: CancelCallback) => void;
  loading: boolean;
}

export const useIntakeRequestActions = (
  refetchQueries?: InternalRefetchQueriesInclude,
): UseIntakePurchaseRequisitionActionsResult => {
  const { confirm } = useConfirmationDialog();
  const { onError } = useCustomSnackbar();
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestAction' });
  const [discard, { loading: discardLoading }] = useMutation(INTAKE_DISCARD_MUTATION, { onError, refetchQueries });
  const [cancel, { loading: cancelLoading }] = useMutation(INTAKE_CANCEL_MUTATION, { onError, refetchQueries });

  return {
    discard: useCallback(
      (id: string, onDiscarded?: DisacrdCallback) =>
        confirm(
          async () => {
            const { data, errors } = await discard({
              variables: { data: { id } },
              update(cache, { data }) {
                const idToRemove = data?.sevenbridges.chatSession.discard.id;
                if (!idToRemove) return;

                const foundQuery = cache.readQuery({ query: INTAKE_THREAD_LIST_QUERY });

                const previous = foundQuery?.sevenbridges.threadList;
                if (!previous) return;

                const filteredNodes = previous?.nodes.filter((item) => item.id !== idToRemove);

                cache.writeQuery({
                  query: INTAKE_THREAD_LIST_QUERY,
                  data: {
                    sevenbridges: {
                      threadList: {
                        nodes: filteredNodes,
                        totalCount: filteredNodes.length,
                      },
                    },
                  },
                });
              },
            });
            if (!errors) onDiscarded?.(data);
          },
          {
            title: t(`discard.title`),
            message: t(`discard.message`),
          },
        )(),
      [confirm, discard, t],
    ),
    cancel: useCallback(
      (id: string, onCancled?: CancelCallback) =>
        confirm(
          async () => {
            const { data, errors } = await cancel({
              variables: { data: { id } },
              update(cache, { data }) {
                const { id, status } = data?.sevenbridges.chatSession.cancel || {};

                cache.modify({
                  // Identify the actual SvbrgThreadItem object to update
                  id: cache.identify({ __typename: 'SvbrgThreadItem', id }),
                  fields: {
                    status() {
                      return status;
                    },
                  },
                });
              },
            });
            if (!errors) onCancled?.(data);
          },
          {
            title: t(`cancel.title`),
            message: t(`cancel.message`),
          },
        )(),
      [cancel, confirm, t],
    ),
    loading: discardLoading || cancelLoading,
  };
};
